import { supabase } from '@/lib/supabase';
import { PRIVATE_AUDIO_BUCKET } from '@/lib/audioUrl';

// Private audio (user-audio bucket) cleanup after a server-side delete —
// contract .claude/qa/erasure-audio-contract.md §3–§6, migration 177.
// Mirrors the storage helpers in lib/repositories/*_repository.dart.
//
// 177 is a draft: every RPC here may be missing (PGRST202 / 42883). Callers
// fall back to today's behaviour then. Storage failures are never shown to the
// user — the words trigger has queued the object and the worker removes it.

/** Storage API delete batch limit (contract §5: chunks of ≤ 1000). */
export const STORAGE_REMOVE_CHUNK = 1000;

/** PostgREST / Postgres "no such function" — 177 is not on this server. */
export function isMissingRpc(e: unknown): boolean {
  const o = (e ?? {}) as { code?: unknown; message?: unknown };
  if (o.code === 'PGRST202' || o.code === '42883') return true;
  return typeof o.message === 'string' && /could not find the function/i.test(o.message);
}

/** Removes the caller's own objects in chunks. Never throws; returns the
 *  names whose chunk the Storage API accepted (for the ack). */
export async function removePrivateAudio(paths: string[]): Promise<string[]> {
  const removed: string[] = [];
  for (let i = 0; i < paths.length; i += STORAGE_REMOVE_CHUNK) {
    const chunk = paths.slice(i, i + STORAGE_REMOVE_CHUNK);
    try {
      const { error } = await supabase.storage.from(PRIVATE_AUDIO_BUCKET).remove(chunk);
      if (error) console.warn('private audio remove failed (queued for the worker):', error);
      else removed.push(...chunk);
    } catch (e) {
      console.warn('private audio remove failed (queued for the worker):', e);
    }
  }
  return removed;
}

/** Drops the cleanup-queue rows of objects that are now gone. Best effort:
 *  missing RPC (no 177) and every other error are ignored. */
export async function ackStorageCleanup(names: string[]): Promise<void> {
  for (let i = 0; i < names.length; i += STORAGE_REMOVE_CHUNK) {
    try {
      const { error } = await supabase.rpc('storage_cleanup_ack', { p_names: names.slice(i, i + STORAGE_REMOVE_CHUNK) });
      if (error) {
        if (!isMissingRpc(error)) console.warn('storage_cleanup_ack failed:', error);
        return;
      }
    } catch (e) {
      console.warn('storage_cleanup_ack failed:', e);
      return;
    }
  }
}

/** Remove, then ack what was removed. Never throws. */
export async function removeAndAckPrivateAudio(paths: string[]): Promise<void> {
  if (paths.length === 0) return;
  const removed = await removePrivateAudio(paths);
  if (removed.length) await ackStorageCleanup(removed);
}

/** "already exists" from the Storage copy endpoint (storage-api 1.x answers
 *  409 Duplicate / "The resource already exists"). */
export function isAlreadyExists(e: unknown): boolean {
  const o = (e ?? {}) as { message?: unknown; statusCode?: unknown; status?: unknown; error?: unknown };
  if (String(o.statusCode ?? '') === '409' || o.status === 409) return true;
  const text = `${typeof o.message === 'string' ? o.message : ''} ${typeof o.error === 'string' ? o.error : ''}`;
  return /already exists|duplicate/i.test(text);
}

/**
 * After an approved concept promotion (contract §6): copy each pending move's
 * source to its global destination and repoint the word. Sequential, usually
 * 0–2 moves. Every failure is swallowed and logged — the promotion has
 * succeeded either way; the word keeps playing from its old path and the
 * worker retries. Returns the number of words repointed (for tests).
 */
export async function runPendingAudioMoves(): Promise<number> {
  let rows: Record<string, unknown>[];
  try {
    const { data, error } = await supabase.rpc('pending_audio_moves');
    if (error) {
      if (!isMissingRpc(error)) console.warn('pending_audio_moves failed:', error);
      return 0;
    }
    rows = (data as Record<string, unknown>[] | null) ?? [];
  } catch (e) {
    console.warn('pending_audio_moves failed:', e);
    return 0;
  }
  let done = 0;
  for (const r of rows) {
    const wordId = r['word_id'] as number;
    const src = r['src_name'] as string;
    const dst = r['dst_name'] as string;
    if (wordId == null || !src || !dst) continue;
    try {
      const { error: copyErr } = await supabase.storage.from(PRIVATE_AUDIO_BUCKET).copy(src, dst);
      if (copyErr && !isAlreadyExists(copyErr)) {
        console.warn('audio move copy failed:', copyErr);
        continue;
      }
      const { data, error } = await supabase.rpc('complete_audio_move', { p_word_id: wordId });
      if (error) { console.warn('complete_audio_move failed:', error); continue; }
      if (data === true) done++;
    } catch (e) {
      console.warn('audio move failed:', e);
    }
  }
  return done;
}
